import { TextInput, type TextInputProps } from '@vtex/admin-ui'
import {
  FormState,
  getErrorText,
  hasError,
  useWatch,
} from '@vtex/admin-ui-form'

import { useCallback, useEffect, useState } from 'react'
import { MAX_DECIMALS_DIGITS, useNumbers } from '../utils/useNumbers'
import './styles.css'
import { truncateDecimals } from './truncateDecimals'

interface NumberInputProps extends TextInputProps {
  state: FormState
  name: string
  fractionDigits?: number
}

/**
 * TextInput abstraction that accepts only numbers, with a defined fraction digits and formatted by locale.
 * One of the key differences from the NumberInput is that it doesn't have arrows to increase or decrease the value.
 */
export function NumberInput(props: NumberInputProps) {
  const { name, state, fractionDigits = MAX_DECIMALS_DIGITS, ...rest } = props

  const { toLocaleNumber, formatNumber } = useNumbers()

  const value: number | undefined = useWatch({ form: state, name })

  const formatValue = useCallback(
    (number: number | undefined): string =>
      number === undefined
        ? ''
        : formatNumber(number, {
            maximumFractionDigits: fractionDigits,
          }),
    [formatNumber, fractionDigits]
  )

  const [inputValue, setInputValue] = useState<string>(formatValue(value))

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      state.setValue(name, undefined)
      setInputValue('')
      return
    }
    const newInputValue = toLocaleNumber(event.target.value)
    const newStateValue = truncateDecimals(newInputValue, fractionDigits)
    state.setValue(name, newStateValue)
    setInputValue(formatValue(newInputValue))
  }

  useEffect(() => {
    setInputValue(formatValue(value))
  }, [formatValue, value])

  return (
    <TextInput
      {...rest}
      name={name}
      value={inputValue}
      onChange={(event) => setInputValue(event.target.value)}
      onBlur={handleBlur}
      error={hasError(state, name)}
      errorText={getErrorText(state, name)}
    />
  )
}
